import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Send, Eye, Lock, CheckCircle2, Clock, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  submitted: { txt: "Indsendt", cls: "bg-amber-50 text-amber-700" },
  contracts_sent: { txt: "Kontrakter sendt", cls: "bg-emerald-50 text-emerald-700" },
};

export default function EmploymentOnboardingsPanel({ token }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [generating, setGenerating] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employment-onboardings`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.onboardings || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const openDetail = async (onb) => {
    setSelected(onb.id);
    try {
      const { data } = await axios.get(`${API}/admin/employment-onboardings/${onb.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDetail(data);
    } catch (e) { toast.error("Kunne ikke hente detaljer"); }
  };

  const generateContracts = async () => {
    if (!detail) return;
    if (!confirm(`Send EMA + Burd kontrakter til ${detail.email}? Dette kan ikke fortrydes.`)) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/employment-onboardings/${detail.id}/generate-contracts`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.ok) {
        toast.success(`Kontrakter sendt til ${data.contracts_sent_to}`);
        await load();
        await openDetail({ id: detail.id });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke sende kontrakter");
    } finally { setGenerating(false); }
  };

  const photoUrl = (kind) => `${API}/admin/employment-onboardings/${detail?.id}/photos/${kind}`;
  const photoExists = (kind) => detail?.photos?.some((p) => p.kind === kind);

  if (loading) return <div data-testid="emp-onboarding-loading" className="text-brand-text2">Indlæser onboardings...</div>;

  return (
    <div data-testid="employment-onboardings-panel" className="space-y-6">
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Ansættelses-onboarding</div>
            <div className="font-display text-lg text-brand-dark">Medarbejdere klar til kontrakt</div>
          </div>
          <div className="text-right text-sm">
            <div className="font-semibold text-brand-dark">{items.length} total</div>
            <div className="text-brand-text2">{items.filter(i => i.status === "submitted").length} venter på kontrakt</div>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-brand p-8 text-center text-brand-text2">
          Ingen onboardings endnu. Send et onboarding-link fra fanen "Ansøgninger" når du har godkendt en chauffør.
        </div>
      ) : (
        <div className="bg-white border border-brand">
          <table className="w-full text-sm">
            <thead className="border-b border-brand">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-widest text-brand-text2">Navn</th>
                <th className="p-3 text-xs uppercase tracking-widest text-brand-text2">By</th>
                <th className="p-3 text-xs uppercase tracking-widest text-brand-text2">Nummerplade</th>
                <th className="p-3 text-xs uppercase tracking-widest text-brand-text2">Indsendt</th>
                <th className="p-3 text-xs uppercase tracking-widest text-brand-text2">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const stat = STATUS_LABEL[it.status] || { txt: it.status, cls: "bg-slate-100 text-slate-700" };
                return (
                  <tr key={it.id} className="border-b border-brand last:border-b-0">
                    <td className="p-3 font-semibold text-brand-dark">{it.full_name}</td>
                    <td className="p-3 text-brand-text2">{it.city}</td>
                    <td className="p-3 font-mono text-xs">{it.license_plate}</td>
                    <td className="p-3 text-xs text-brand-text2">{(it.submitted_at || "").slice(0, 10)}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-1 ${stat.cls}`}>{stat.txt}</span></td>
                    <td className="p-3 text-right">
                      <button onClick={() => openDetail(it)} data-testid={`emp-open-${it.id}`} className="text-brand-red font-semibold text-xs hover:underline">Åbn →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="max-w-3xl mx-auto my-10 bg-white p-8" onClick={(e) => e.stopPropagation()} data-testid="emp-detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="kicker text-brand-red">Medarbejder · Onboarding</div>
                <h2 className="font-display text-2xl text-brand-dark">{detail.full_name}</h2>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-brand-text2 hover:text-brand-dark text-xl">×</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-brand-bg p-4 mb-4">
              <div><div className="text-xs text-brand-text2 uppercase">Email</div><div className="font-semibold text-brand-dark">{detail.email}</div></div>
              <div><div className="text-xs text-brand-text2 uppercase">Mobil</div><div className="font-semibold">{detail.phone}</div></div>
              <div className="sm:col-span-2"><div className="text-xs text-brand-text2 uppercase">Adresse</div><div className="font-semibold">{detail.address}, {detail.zip} {detail.city}</div></div>
              <div><div className="text-xs text-brand-text2 uppercase flex items-center gap-1"><Lock size={10} /> CPR</div><div className="font-mono font-semibold">{detail.cpr}</div></div>
              <div><div className="text-xs text-brand-text2 uppercase">Nummerplade</div><div className="font-mono font-semibold">{detail.license_plate}</div></div>
              <div><div className="text-xs text-brand-text2 uppercase flex items-center gap-1"><Lock size={10} /> Reg.nr</div><div className="font-mono">{detail.bank_reg}</div></div>
              <div><div className="text-xs text-brand-text2 uppercase flex items-center gap-1"><Lock size={10} /> Konto</div><div className="font-mono">{detail.bank_account}</div></div>
            </div>

            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold mb-2">Dokumenter</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {["criminal_record","drivers_license_front","drivers_license_back","residence_permit","passport_photo"].map((k) => {
                const exists = photoExists(k);
                const labels = {criminal_record:"Straffeattest",drivers_license_front:"Kørekort F",drivers_license_back:"Kørekort B",residence_permit:"Opholdstill.",passport_photo:"Pasbillede"};
                return exists ? (
                  <a key={k} href={photoUrl(k)} target="_blank" rel="noreferrer" className="border border-brand p-3 hover:border-brand-dark text-sm" data-testid={`emp-doc-${k}`}>
                    <Eye size={14} className="text-brand-red mb-1" />
                    <div className="font-semibold text-brand-dark text-xs">{labels[k]}</div>
                    <div className="text-[10px] text-brand-text2">Åbn →</div>
                  </a>
                ) : (
                  <div key={k} className="border border-dashed border-brand p-3 text-sm opacity-50">
                    <div className="font-semibold text-brand-text2 text-xs">{labels[k]}</div>
                    <div className="text-[10px]">Mangler</div>
                  </div>
                );
              })}
            </div>

            {detail.status === "contracts_sent" ? (
              <div className="bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 size={18} />
                Kontrakter blev sendt til {detail.email} den {(detail.contracts_sent_at || "").slice(0, 10)}.
              </div>
            ) : (
              <button
                onClick={generateContracts}
                disabled={generating}
                className="btn-primary w-full justify-center"
                data-testid="emp-generate-contracts"
              >
                {generating ? <>Genererer...</> : <><Send size={14} /> Generér EMA + Burd kontrakter og send via email</>}
              </button>
            )}

            <p className="text-xs text-brand-text2 mt-4 flex items-center gap-2">
              <Lock size={10} /> CPR + bankoplysninger må kun vises for bemyndiget HR-personale (GDPR Art. 32).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
